"use client";

import React, { useState, useEffect } from "react";
import { notifications } from "@mantine/notifications";
import {
  useCreateBulkPromotionMutation,
  useDisableBulkPromotionMutation,
  useGetBulkPromotionsQuery,
  useUpdateBulkPromotionMutation,
} from "Services/Admin/BulkPromotionApiSlice";
import { BulkPromotionRequestDTO } from "Types/Admin/BulkPromotionType";
import { useAppSelector } from "hooks";
import { useGetAllLocTypesQuery } from "Services/Admin/LocTypeApiSlice";
import { useGetAllAdminProductsQuery } from "Services/Admin/ProductAdminApiSlice";
import { Header } from "Components/Admin/StyleComponent/Header";
import { useEcomConfig } from "hooks/useEcomConfig";
import useDebounce from "hooks/useDebounce";

export const AdminBulkProduct = () => {
  const config = useAppSelector((state: any) => state.configuration.config);
  const plant = process.env.REACT_APP_PLANT;

  const getDateTimeInputValue = (date?: string, time?: string) => {
    if (!date) return "";
    const safeTime = (time || "00:00").slice(0, 5);

    if (date.includes("/")) {
      const [day, month, year] = date.split("/");
      if (!day || !month || !year) return "";
      return (
        year +
        "-" +
        month.padStart(2, "0") +
        "-" +
        day.padStart(2, "0") +
        "T" +
        safeTime
      );
    }

    if (date.includes("-")) {
      return date + "T" + safeTime;
    }

    return "";
  };

  const formatDateTimeForApi = (dateTimeValue: string) => {
    if (!dateTimeValue) {
      return { date: "", time: "" };
    }

    const [datePart, timePart = "00:00"] = dateTimeValue.split("T");
    const [year, month, day] = datePart.split("-");

    return {
      date: day + "/" + month + "/" + year,
      time: timePart.slice(0, 5),
    };
  };

  const [formData, setFormData] = useState({
    promotionName: "",
    promotionDesc: "",
    outlet: "",
    startDate: "",
    endDate: "",
    isActive: "Y",
    buyQty: "",
    promotionType: "",
    promotionValue: "",
    limitOfUsage: "",
    usageUsed: "",
    startTime: "",
    endTime: "",
    buyItemIds: [] as string[],
    promotion: 0,
  });

  const [selectedProducts, setSelectedProducts] = useState<string[]>([]);
  const [searchFilters, setSearchFilters] = useState({
    productId: "",
    title: "",
    language: "",
    subCategory: "",
    productName: "",
    author: "",
    category: "",
    brand: "",
  });
  const [showAdvancedSearch, setShowAdvancedSearch] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(100);
  const [allProducts, setAllProducts] = useState<any[]>([]);
  const [filteredProducts, setFilteredProducts] = useState<any[]>([]);
  const [resolvedPromotionId, setResolvedPromotionId] = useState<number | null>(
    null,
  );
  const [loadedExistingPromotion, setLoadedExistingPromotion] = useState(false);
  const [isTransitioning, setIsTransitioning] = useState(false);

  const triggerTransition = () => {
    setIsTransitioning(true);
    setTimeout(() => setIsTransitioning(false), 300);
  };

  const [createBulkPromotion, { isLoading: isCreating }] =
    useCreateBulkPromotionMutation();
  const [updateBulkPromotion, { isLoading: isUpdating }] =
    useUpdateBulkPromotionMutation();
  const [disableBulkPromotion, { isLoading: isDisabling }] =
    useDisableBulkPromotionMutation();
  const { showPromotion } = useEcomConfig();
  const {
    data: existingBulkPromotionData,
    refetch: refetchBulkPromotion,
    isLoading: isPromotionsLoading,
  } = useGetBulkPromotionsQuery(
    undefined,
    { skip: !showPromotion },
  );

  const { data: locTypes = [], isLoading: isLoadingLocations } =
    useGetAllLocTypesQuery({
      plant: plant,
    });

  // Same server-side search as the Product page: the first filled-in advanced
  // search field is sent as `search`; the remaining fields refine the
  // returned page client-side.
  const serverSearchTerm =
    [
      searchFilters.productId,
      searchFilters.productName,
      searchFilters.title,
      searchFilters.author,
      searchFilters.category,
      searchFilters.subCategory,
      searchFilters.language,
      searchFilters.brand,
    ].find((value) => value.trim())?.trim() || "";
  const debouncedSearch = useDebounce(serverSearchTerm, 500);

  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearch]);

  const { data: productsData, isLoading: isLoadingProducts, isFetching: isFetchingProducts, refetch: refetchProducts } =
    useGetAllAdminProductsQuery({
      category: "",
      subCategory: "",
      brand: "",
      activePage: currentPage,
      pageSize: itemsPerPage,
      search: debouncedSearch,
    });

  const existingBulkPromotion = Array.isArray(
    (existingBulkPromotionData as any)?.results,
  )
    ? (existingBulkPromotionData as any)?.results?.[0]
    : (existingBulkPromotionData as any)?.results || null;

  const getPromotionIdFromResponse = (data: any): number | null => {
    const candidates = [
      data?.id,
      data?.promotionId,
      data?.hdrId,
      data?.promotionHdr?.id,
    ];

    const validId = candidates.find(
      (value) =>
        typeof value === "number" && Number.isFinite(value) && value > 0,
    );

    return typeof validId === "number" ? validId : null;
  };

  const hasExistingPromotion =
    !!existingBulkPromotion &&
    typeof existingBulkPromotion === "object" &&
    Object.keys(existingBulkPromotion).length > 0;

  const normalizePromotionPayload = (data: any): BulkPromotionRequestDTO => ({
    promotionName: data?.promotionName || "",
    promotionDescription: data?.promotionDescription || "",
    outlet: data?.outlet || "",
    startDate: data?.startDate || "",
    startTime: data?.startTime || "",
    endDate: data?.endDate || "",
    endTime: data?.endTime || "",
    isActive: data?.isActive || "Y",
    buyQty: Number(data?.buyQty) || 0,
    promotionType: data?.promotionType || "",
    promotion: Number(data?.promotion) || 0,
    limitOfUsage: Number(data?.limitOfUsage) || 0,
    buyItemIds: Array.isArray(data?.buyItemIds) ? data.buyItemIds : [],
  });

  const hasOnlyActiveStatusChanged = (
    oldData: BulkPromotionRequestDTO,
    nextData: BulkPromotionRequestDTO,
  ) => {
    const sameArray = (a: string[], b: string[]) => {
      const sortedA = [...a].sort();
      const sortedB = [...b].sort();
      return (
        sortedA.length === sortedB.length &&
        sortedA.every((value, index) => value === sortedB[index])
      );
    };

    const unchanged =
      oldData.promotionName === nextData.promotionName &&
      oldData.promotionDescription === nextData.promotionDescription &&
      oldData.outlet === nextData.outlet &&
      oldData.startDate === nextData.startDate &&
      oldData.startTime === nextData.startTime &&
      oldData.endDate === nextData.endDate &&
      oldData.endTime === nextData.endTime &&
      oldData.buyQty === nextData.buyQty &&
      oldData.promotionType === nextData.promotionType &&
      oldData.promotion === nextData.promotion &&
      oldData.limitOfUsage === nextData.limitOfUsage &&
      sameArray(oldData.buyItemIds, nextData.buyItemIds);

    return unchanged && oldData.isActive !== nextData.isActive;
  };

  useEffect(() => {
    if (!existingBulkPromotion || loadedExistingPromotion) return;

    setFormData({
      promotionName: existingBulkPromotion.promotionName || "",
      promotionDesc: existingBulkPromotion.promotionDescription || "",
      outlet: existingBulkPromotion.outlet || "",
      startDate: getDateTimeInputValue(
        existingBulkPromotion.startDate,
        existingBulkPromotion.startTime,
      ),
      endDate: getDateTimeInputValue(
        existingBulkPromotion.endDate,
        existingBulkPromotion.endTime,
      ),
      isActive: existingBulkPromotion.isActive || "Y",
      buyQty: existingBulkPromotion.buyQty?.toString() || "",
      promotionType: existingBulkPromotion.promotionType || "",
      promotionValue: existingBulkPromotion.promotion?.toString() || "",
      limitOfUsage: existingBulkPromotion.limitOfUsage?.toString() || "",
      usageUsed: existingBulkPromotion.usageUsed?.toString() || "",
      startTime: existingBulkPromotion.startTime || "",
      endTime: existingBulkPromotion.endTime || "",
      buyItemIds: existingBulkPromotion.buyItemIds || [],
      promotion: existingBulkPromotion.promotion || 0,
    });

    setSelectedProducts(existingBulkPromotion.buyItemIds || []);

    const existingId = getPromotionIdFromResponse(existingBulkPromotion);
    if (existingId) {
      setResolvedPromotionId(existingId);
    }

    setLoadedExistingPromotion(true);
  }, [existingBulkPromotion, loadedExistingPromotion]);
  useEffect(() => {
    const productRows = Array.isArray((productsData as any)?.products)
      ? (productsData as any).products
      : Array.isArray((productsData as any)?.results?.products)
        ? (productsData as any).results.products
        : [];

    setAllProducts(productRows);
    setFilteredProducts(productRows);
  }, [productsData]);

  useEffect(() => {
    let filtered = [...allProducts];

    if (searchFilters.productId) {
      filtered = filtered.filter((p) =>
        p.product.item
          .toLowerCase()
          .includes(searchFilters.productId.toLowerCase()),
      );
    }
    if (searchFilters.title) {
      filtered = filtered.filter((p) =>
        p.product.title
          ?.toLowerCase()
          .includes(searchFilters.title.toLowerCase()),
      );
    }
    if (searchFilters.language) {
      filtered = filtered.filter((p) =>
        p.product.language
          ?.toLowerCase()
          .includes(searchFilters.language.toLowerCase()),
      );
    }
    if (searchFilters.subCategory) {
      filtered = filtered.filter((p) => {
        const filterValue = searchFilters.subCategory.toLowerCase();
        const subClassName = p.product.subClassName?.toLowerCase() || "";
        const subClassId = p.product.subClassId?.toLowerCase() || "";
        const subCategory = p.product.subCategory?.toLowerCase() || "";
        return (
          subClassName.includes(filterValue) ||
          subClassId.includes(filterValue) ||
          subCategory.includes(filterValue)
        );
      });
    }
    if (searchFilters.productName) {
      filtered = filtered.filter((p) =>
        p.product.itemDesc
          ?.toLowerCase()
          .includes(searchFilters.productName.toLowerCase()),
      );
    }
    if (searchFilters.author) {
      filtered = filtered.filter((p) =>
        p.product.author
          ?.toLowerCase()
          .includes(searchFilters.author.toLowerCase()),
      );
    }
    if (searchFilters.category) {
      filtered = filtered.filter((p) => {
        const categoryName = p.product.categoryName?.toLowerCase() || "";
        const categoryCode = p.product.category?.toLowerCase() || "";
        const filterValue = searchFilters.category.toLowerCase();
        return (
          categoryName.includes(filterValue) ||
          categoryCode.includes(filterValue)
        );
      });
    }
    if (searchFilters.brand && config?.industryType === "Textile") {
      filtered = filtered.filter((p) =>
        p.product.brand
          ?.toLowerCase()
          .includes(searchFilters.brand.toLowerCase()),
      );
    }

    setFilteredProducts(filtered);
  }, [searchFilters, allProducts, config?.industryType]);

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >,
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSearchChange =
    (key: keyof typeof searchFilters) =>
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const { value } = e.target;
      setSearchFilters((prev) => ({
        ...prev,
        [key]: value,
      }));
    };

  const handleProductSelect = (productId: string) => {
    setSelectedProducts((prev) => {
      if (prev.includes(productId)) {
        return prev.filter((id) => id !== productId);
      } else {
        return [...prev, productId];
      }
    });
  };

  const handleSelectAll = (e: React.ChangeEvent<HTMLInputElement>) => {
    // Only toggle the current page so selections on other pages are kept.
    const pageIds: string[] = paginatedProducts.map((p) => p.product.item);
    if (e.target.checked) {
      setSelectedProducts((prev) => Array.from(new Set([...prev, ...pageIds])));
    } else {
      setSelectedProducts((prev) => prev.filter((id) => !pageIds.includes(id)));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (
      !formData.promotionName ||
      !formData.promotionDesc ||
      !formData.outlet
    ) {
      return;
    }

    if (selectedProducts.length === 0) {
      return;
    }

    const startDateValue = new Date(formData.startDate);
    const endDateValue = new Date(formData.endDate);

    if (isNaN(startDateValue.getTime()) || isNaN(endDateValue.getTime())) {
      return;
    }

    if (endDateValue < startDateValue) {
      return;
    }

    // Validate start/end date range before sending to API
    if (!formData.startDate || !formData.endDate) {
      return;
    }

    const startDate = new Date(formData.startDate);
    const endDate = new Date(formData.endDate);

    if (Number.isNaN(startDate.getTime()) || Number.isNaN(endDate.getTime())) {
      return;
    }

    if (endDate < startDate) {
      return;
    }

    const startDateTime = formatDateTimeForApi(formData.startDate);
    const endDateTime = formatDateTimeForApi(formData.endDate);

    const promotionData: BulkPromotionRequestDTO = {
      promotionName: formData.promotionName,
      promotionDescription: formData.promotionDesc,
      outlet: formData.outlet,
      startDate: startDateTime.date,
      startTime: startDateTime.time,
      endDate: endDateTime.date,
      endTime: endDateTime.time,
      isActive: formData.isActive,
      buyQty: Number(formData.buyQty) || 0,
      promotionType: formData.promotionType,
      promotion: Number(formData.promotionValue) || 0,
      limitOfUsage: Number(formData.limitOfUsage) || 0,
      buyItemIds: selectedProducts,
    };

    const existingDataForCompare = hasExistingPromotion
      ? normalizePromotionPayload(existingBulkPromotion)
      : null;
    const onlyActiveStatusChanged =
      !!existingDataForCompare &&
      hasOnlyActiveStatusChanged(existingDataForCompare, promotionData);

    const callPut = async () => {
      const isDisableTransition =
        onlyActiveStatusChanged &&
        existingDataForCompare?.isActive === "Y" &&
        promotionData.isActive === "N";

      if (isDisableTransition) {
        return (disableBulkPromotion as any)(undefined).unwrap();
      }
      return (updateBulkPromotion as any)(promotionData).unwrap();
    };

    try {
      let response;

      if (hasExistingPromotion) {
        response = await callPut();
      } else {
        response = await createBulkPromotion(promotionData).unwrap();
        const createdId = getPromotionIdFromResponse(
          (response as any)?.results,
        );

        if (createdId) {
          setResolvedPromotionId(createdId);
        } else {
          const refreshed = await refetchBulkPromotion();
          const fetchedPromotion = Array.isArray(
            (refreshed.data as any)?.results,
          )
            ? (refreshed.data as any).results[0]
            : (refreshed.data as any)?.results;

          const refetchedId = getPromotionIdFromResponse(fetchedPromotion);
          if (refetchedId) {
            setResolvedPromotionId(refetchedId);
          }
        }
      }

      refetchProducts();

      notifications.show({
        message: response?.message,
        color: "green",
      });
    } catch (error) {
      const isConflict =
        (error as any)?.status === 409 ||
        (error as any)?.originalStatus === 409 ||
        (error as any)?.data?.statusCode === 409;

      if (isConflict) {
        try {
          const refreshed = await refetchBulkPromotion();
          const fetchedPromotion = Array.isArray(
            (refreshed.data as any)?.results,
          )
            ? (refreshed.data as any).results[0]
            : (refreshed.data as any)?.results;
          if (fetchedPromotion) {
            const response: any = await callPut();
            refetchProducts();
            notifications.show({
              message: response?.message,
              color: "green",
            });
            return;
          }
        } catch (fallbackError) {
          console.error(
            "Fallback update/disable failed after 409:",
            fallbackError,
          );
        }
      }

      console.error("Failed to save bulk promotion:", error);
      notifications.show({
        message: (error as any)?.data?.message || "An error occurred",
        color: "red",
      });
    }
  };
  const isSaving = isCreating || isUpdating || isDisabling;

  const isLoading =
    isLoadingProducts ||
    isLoadingLocations ||
    isPromotionsLoading;

  // Pagination is server-side (same as the Product page): the API returns one
  // page of rows plus the overall total.
  const totalItems = (productsData as any)?.totalProducts || 0;
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const paginatedProducts = filteredProducts;

  return (
    <div className="font-body p-6 mx-auto h-full">
      <div className="mb-6">
        <Header title="Bulk Product Promotion" />
      </div>
      <form onSubmit={handleSubmit} className="h-full">
        <div className="bg-white rounded-md shadow-md flex flex-col relative h-[calc(100vh-200px)]">
          <div className="p-8 space-y-8 flex-1 overflow-y-auto">
          {Number(formData.usageUsed) >= Number(formData.limitOfUsage) && Number(formData.limitOfUsage) > 0 && (
            <div className="flex items-start gap-3 rounded-md border border-red-300 bg-red-50 px-4 py-4 text-sm text-red-800">
              <svg xmlns="http://www.w3.org/2000/svg" className="mt-0.5 h-5 w-5 shrink-0 text-red-500" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
              <div>
                <p className="font-semibold">Promotion fully claimed.</p>
                <p className="mt-1 text-red-700">All available redemptions for this promotion have been used up. Customers can no longer apply this offer.</p>
              </div>
            </div>
          )}
          {formData.isActive === "N" && (
            <div className="flex items-start gap-3 rounded-md border border-yellow-300 bg-yellow-100 px-4 py-4 text-sm text-yellow-800">
              <svg xmlns="http://www.w3.org/2000/svg" className="mt-0.5 h-5 w-5 shrink-0 text-yellow-500" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M8.485 2.495c.673-1.167 2.357-1.167 3.03 0l6.28 10.875c.673 1.167-.17 2.625-1.516 2.625H3.72c-1.347 0-2.189-1.458-1.515-2.625L8.485 2.495zM10 5a.75.75 0 01.75.75v3.5a.75.75 0 01-1.5 0v-3.5A.75.75 0 0110 5zm0 9a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd" />
              </svg>
              <div>
                <p className="font-semibold">This promotion is currently inactive.</p>
                <p className="mt-1 text-yellow-700">It may have expired or been manually disabled. To reactivate it, update the end date to a future date and time, then set the status back to <strong>Active</strong>.</p>
              </div>
            </div>
          )}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="text-sm text-gray-500">Promotion Name:*</label>
              <input
                type="text"
                name="promotionName"
                value={formData.promotionName}
                onChange={handleChange}
                maxLength={100}
                className="w-full border px-3 py-2 rounded-md text-sm"
                placeholder="Enter promotion name (max 100 chars)"
                required
                disabled={isLoading}
              />
              <div className="text-xs text-gray-400 text-right">
                {formData.promotionName.length}/100 characters
              </div>
            </div>

            <div>
              <label className="text-sm text-gray-500">
                Promotion Description:*
              </label>
              <textarea
                name="promotionDesc"
                value={formData.promotionDesc}
                onChange={handleChange}
                maxLength={200}
                className="w-full border px-3 py-2 rounded-md text-sm"
                placeholder="Enter promotion description (max 200 chars)"
                required
                disabled={isLoading}
              />
              <div className="text-xs text-gray-400 text-right">
                {formData.promotionDesc.length}/200 characters
              </div>
            </div>

            <div>
              <label className="text-sm text-gray-500">Outlet/Location:*</label>
              <select
                name="outlet"
                value={formData.outlet}
                onChange={handleChange}
                className="w-full border px-3 py-2 rounded-md text-sm"
                required
                disabled={isLoading}
              >
                <option value="">Select a location</option>
                {locTypes.map((locType) => (
                  <option key={locType.locTypeId} value={locType.locTypeDesc}>
                    {locType.locTypeDesc}
                  </option>
                ))}
                {formData.outlet &&
                  !locTypes.some(
                    (locType) =>
                      locType.locTypeDesc === formData.outlet ||
                      locType.locTypeId === formData.outlet,
                  ) && (
                    <option value={formData.outlet}>{formData.outlet}</option>
                  )}
              </select>
            </div>

            <div>
              <label className="text-sm text-gray-500">Start Date & Time</label>
              <input
                type="datetime-local"
                name="startDate"
                value={formData.startDate}
                onChange={handleChange}
                className="w-full border px-3 py-2 rounded-md text-sm"
                disabled={isLoading}
              />
            </div>

            <div>
              <label className="text-sm text-gray-500">End Date & Time</label>
              <input
                type="datetime-local"
                name="endDate"
                value={formData.endDate}
                onChange={handleChange}
                className="w-full border px-3 py-2 rounded-md text-sm"
                disabled={isLoading}
              />
            </div>

            <div className="flex items-center gap-6 pt-6">
              <label>
                <input
                  type="radio"
                  name="isActive"
                  value="Y"
                  checked={formData.isActive === "Y"}
                  onChange={handleChange}
                  className="mr-1"
                  disabled={isLoading}
                />
                Active
              </label>
              <label>
                <input
                  type="radio"
                  name="isActive"
                  value="N"
                  checked={formData.isActive === "N"}
                  onChange={handleChange}
                  className="mr-1"
                  disabled={isLoading}
                />
                Non Active
              </label>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="text-sm text-gray-500">Buy Quantity</label>
              <input
                type="number"
                name="buyQty"
                onWheel={(e) => e.currentTarget.blur()}
                value={formData.buyQty || ""}
                onChange={handleChange}
                placeholder="Enter Buy Quantity"
                className="w-full border px-3 py-2 rounded-md text-sm"
                min="1"
                disabled={isLoading}
              />
            </div>

            <div>
              <label className="text-sm text-gray-500">Promotion Type</label>
              <select
                name="promotionType"
                value={formData.promotionType}
                onChange={handleChange}
                className="w-full border px-3 py-2 rounded-md text-sm"
                disabled={isLoading}
              >
                <option value="">Select Promotion Type</option>
                <option value="%">Percentage</option>
                <option value="INR">Fixed Amount</option>
              </select>
            </div>

            <div>
              <label className="text-sm text-gray-500">Promotion Value</label>
              <input
                type="number"
                name="promotionValue"
                onWheel={(e) => e.currentTarget.blur()}
                value={formData.promotionValue || ""}
                onChange={handleChange}
                placeholder="Enter Promotion Value"
                className="w-full border px-3 py-2 rounded-md text-sm"
                min="1"
                step="0.01"
                disabled={isLoading}
              />
            </div>

            <div>
              <label className="text-sm text-gray-500">Limit Of Usage</label>
              <input
                type="number"
                name="limitOfUsage"
                onWheel={(e) => e.currentTarget.blur()}
                value={formData.limitOfUsage || ""}
                onChange={handleChange}
                placeholder="Enter Limit Of Usage"
                className="w-full border px-3 py-2 rounded-md text-sm"
                min="1"
                disabled={isLoading}
              />
            </div>

            <div>
              <label className="text-sm text-gray-500">Usage Used</label>
              <div className="w-full border px-3 py-2 rounded-md text-sm bg-gray-50 text-gray-600 select-none">
                {formData.usageUsed || "0"}
              </div>
            </div>
          </div>

          <div className="space-y-6">
            <div
              className="text-blue-600 text-sm cursor-pointer"
              onClick={() => setShowAdvancedSearch(!showAdvancedSearch)}
            >
              {showAdvancedSearch ? "Hide" : "Show"} Advanced Search &gt;&gt;
            </div>

            {showAdvancedSearch && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div className="space-y-4">
                  <input
                    className="w-full border border-gray-300 px-3 py-2 rounded-md text-sm placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-600 focus:border-blue-200 transition"
                    placeholder="Product ID"
                    value={searchFilters.productId}
                    onChange={handleSearchChange("productId")}
                    disabled={isLoading}
                  />
                  <input
                    className="w-full border border-gray-300 px-3 py-2 rounded-md text-sm placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-600 focus:border-blue-200 transition"
                    placeholder="Title"
                    value={searchFilters.title}
                    onChange={handleSearchChange("title")}
                    disabled={isLoading}
                  />
                  <input
                    className="w-full border border-gray-300 px-3 py-2 rounded-md text-sm placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-600 focus:border-blue-200 transition"
                    placeholder="Language"
                    value={searchFilters.language}
                    onChange={handleSearchChange("language")}
                    disabled={isLoading}
                  />
                  <input
                    className="w-full border border-gray-300 px-3 py-2 rounded-md text-sm placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-600 focus:border-blue-200 transition"
                    placeholder="Sub Category"
                    value={searchFilters.subCategory}
                    onChange={handleSearchChange("subCategory")}
                    disabled={isLoading}
                  />
                </div>

                <div className="space-y-4">
                  <input
                    className="w-full border border-gray-300 px-3 py-2 rounded-md text-sm placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-600 focus:border-blue-200 transition"
                    placeholder="Product Name"
                    value={searchFilters.productName}
                    onChange={handleSearchChange("productName")}
                    disabled={isLoading}
                  />
                  <input
                    className="w-full border border-gray-300 px-3 py-2 rounded-md text-sm placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-600 focus:border-blue-200 transition"
                    placeholder="Author"
                    value={searchFilters.author}
                    onChange={handleSearchChange("author")}
                    disabled={isLoading}
                  />
                  <input
                    className="w-full border border-gray-300 px-3 py-2 rounded-md text-sm placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-600 focus:border-blue-200 transition"
                    placeholder="Category"
                    value={searchFilters.category}
                    onChange={handleSearchChange("category")}
                    disabled={isLoading}
                  />
                  {config?.industryType === "Textile" && (
                    <input
                      className="w-full border border-gray-300 px-3 py-2 rounded-md text-sm placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-600 focus:border-blue-200 transition"
                      placeholder="Brand"
                      value={searchFilters.brand}
                      onChange={handleSearchChange("brand")}
                      disabled={isLoading}
                    />
                  )}
                </div>
              </div>
            )}
          </div>
          <div>
            {(isLoadingProducts || isFetchingProducts || isTransitioning) ? (
              <div className="flex justify-center py-8 relative">
                <div className="absolute inset-0 bg-white/40 z-10 flex items-center justify-center">
                  <div className="w-8 h-8 border-4 border-orange-400 border-t-transparent rounded-full animate-spin"></div>
                </div>
              </div>
            ) : (
              <>
                <div className="overflow-auto">
                  <table className="min-w-full text-sm border">
                    <thead className="bg-gray-100">
                      <tr>
                        <th className="p-2 border">S.No</th>
                        <th className="p-2 border">
                          <input
                            type="checkbox"
                            onChange={handleSelectAll}
                            checked={
                              paginatedProducts.length > 0 &&
                              paginatedProducts.every((p) => selectedProducts.includes(p.product.item))
                            }
                            disabled={isLoading}
                          />
                        </th>
                        <th className="p-2 border">ISBN</th>
                        <th className="p-2 border">Product ID</th>
                        <th className="p-2 border">Product Name</th>
                        <th className="p-2 border">Title</th>
                        <th className="p-2 border">Author</th>
                        <th className="p-2 border">Language</th>
                        <th className="p-2 border">UOM</th>
                        <th className="p-2 border">Category</th>
                        <th className="p-2 border">SubCategory</th>
                        {config?.industryType === "Textile" && (
                          <th className="p-2 border">Brand</th>
                        )}
                      </tr>
                    </thead>
                    <tbody>
                      {paginatedProducts.map((item, index) => {
                        const product = item.product;
                        const productId = product.item;
                        const serialNumber =
                          (currentPage - 1) * itemsPerPage + index + 1;
                        return (
                          <tr
                            key={product.id}
                          >
                            <td className="p-2 border text-center">
                              {serialNumber}
                            </td>
                            <td className="p-2 border text-center">
                              <input
                                type="checkbox"
                                checked={selectedProducts.includes(productId)}
                                onChange={() => handleProductSelect(productId)}
                                disabled={isLoading}
                              />
                            </td>
                            <td className="p-2 border">
                              {product.isbn || "-"}
                            </td>
                            <td className="p-2 border">{product.item}</td>
                            <td className="p-2 border">{product.itemDesc}</td>
                            <td className="p-2 border">
                              {product.title || "-"}
                            </td>
                            <td className="p-2 border">
                              {product.author || "-"}
                            </td>
                            <td className="p-2 border">
                              {product.language || "-"}
                            </td>
                            <td className="p-2 border">
                              {product.salesUom || "-"}
                            </td>
                            <td className="p-2 border">
                              {product.categoryName || "-"}
                            </td>
                            <td className="p-2 border">
                              {product.subClassName || "-"}
                            </td>
                            {config?.industryType === "Textile" && (
                              <td className="p-2 border">
                                {product.brand || "-"}
                              </td>
                            )}
                          </tr>
                        );
                      })}
                      {paginatedProducts.length === 0 && (
                        <tr>
                          <td
                            colSpan={
                              config?.industryType === "Textile" ? 12 : 11
                            }
                            className="p-4 text-center text-gray-500"
                          >
                            No products found
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>

                <div className="flex justify-between items-center pt-4 text-sm">
                  <div>
                    Showing{" "}
                    {totalItems === 0 ? 0 : (currentPage - 1) * itemsPerPage + 1} to{" "}
                    {Math.min(currentPage * itemsPerPage, totalItems)} of{" "}
                    {totalItems} entries
                  </div>
                  <div className="flex items-center gap-2">
                    <select
                      className="border px-2 py-1 rounded"
                      value={itemsPerPage}
                      onChange={(e) => {
                        setItemsPerPage(Number(e.target.value));
                        setCurrentPage(1);
                        triggerTransition();
                      }}
                      disabled={isLoading}
                    >
                      <option value={100}>100</option>
                      <option value={500}>500</option>
                      <option value={1000}>1000</option>
                      <option value={1500}>1500</option>
                    </select>
                    <button
                      type="button"
                      className="px-2 border rounded disabled:opacity-50"
                      onClick={() => {
                        setCurrentPage(1);
                        triggerTransition();
                      }}
                      disabled={currentPage === 1 || isLoading}
                    >
                      &lt;&lt;
                    </button>
                    <button
                      type="button"
                      className="px-2 border rounded disabled:opacity-50"
                      onClick={() => {
                        setCurrentPage((prev) => Math.max(1, prev - 1));
                        triggerTransition();
                      }}
                      disabled={currentPage === 1 || isLoading}
                    >
                      &lt;
                    </button>
                    <span className="px-2">
                      Page {currentPage} of {totalPages || 1}
                    </span>
                    <button
                      type="button"
                      className="px-2 border rounded disabled:opacity-50"
                      onClick={() => {
                        setCurrentPage((prev) => Math.min(totalPages, prev + 1));
                        triggerTransition();
                      }}
                      disabled={
                        currentPage === totalPages ||
                        totalPages === 0 ||
                        isLoading
                      }
                    >
                      &gt;
                    </button>
                    <button
                      type="button"
                      className="px-2 border rounded disabled:opacity-50"
                      onClick={() => {
                        setCurrentPage(totalPages);
                        triggerTransition();
                      }}
                      disabled={
                        currentPage === totalPages ||
                        totalPages === 0 ||
                        isLoading
                      }
                    >
                      &gt;&gt;
                    </button>
                  </div>
                </div>

              </>
            )}
          </div>

        </div>
        
        <div className="bg-white border-t px-6 py-4 z-10 rounded-b-md shrink-0 shadow-[0_-4px_6px_-1px_rgba(0,0,0,0.05)]">
          <div className="flex justify-end gap-3">
            <button
              type="button"
              disabled={isLoading}
              onClick={() => window.history.back()}
              className={`px-6 py-2 text-sm font-medium border border-gray-300 rounded-md bg-white text-blue-600 hover:bg-gray-50 ${
                isLoading ? "opacity-50 cursor-not-allowed" : ""
              }`}
            >
              Cancel
            </button>

            <button
              type="submit"
              disabled={isLoading || isSaving}
              onClick={handleSubmit}
              className={`flex items-center justify-center gap-2 px-6 py-2 bg-blue-600 text-white text-sm font-medium rounded-md transition-all ${
                isLoading || isSaving
                  ? "bg-blue-400 cursor-not-allowed"
                  : "hover:bg-blue-700"
              }`}
            >
              {isSaving && (
                <svg
                  className="animate-spin h-4 w-4 text-white"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  ></circle>
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  ></path>
                </svg>
              )}
              {isSaving
                ? "Saving..."
                : hasExistingPromotion || resolvedPromotionId
                  ? "Update"
                  : "Save"}
            </button>
          </div>
        </div>
      </div>
    </form>
    </div>
  );
};

export default AdminBulkProduct;
